import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Empty, Pagination, Table } from 'antd';
import supplierProfileService from '../../../../services/supplierProfileService';
import {
  PAYMENT_STATUS_LABELS,
  TRANSACTION_TYPE_LABELS,
} from '../../../../services/purchaseDealSheetService';
import {
  formatCurrency,
  formatDate,
} from '../../pre-ipo/purchase-deal-sheet/purchaseDealSheet.helpers';

export default function SupplierTransactionsTab({ supplierNumericId }) {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const fetchTransactions = useCallback(async () => {
    if (!supplierNumericId) return;
    setLoading(true);
    setError('');
    try {
      const data = await supplierProfileService.listTransactions(supplierNumericId, {
        page,
        limit: pageSize,
      });
      setItems(Array.isArray(data?.items) ? data.items : []);
      setPagination(data?.pagination ?? null);
    } catch (err) {
      setItems([]);
      setPagination(null);
      setError(err?.response?.data?.message || 'Failed to load transactions.');
    } finally {
      setLoading(false);
    }
  }, [supplierNumericId, page, pageSize]);

  useEffect(() => {
    fetchTransactions();
  }, [fetchTransactions]);

  const columns = useMemo(
    () => [
      {
        title: 'ID',
        dataIndex: 'id',
        key: 'id',
        width: 72,
        render: (id) => <span className="font-mono text-xs text-gray-600">{id ?? '—'}</span>,
      },
      {
        title: 'Date',
        dataIndex: 'transaction_date',
        key: 'transaction_date',
        width: 130,
        render: (value) => formatDate(value),
      },
      {
        title: 'Security',
        key: 'security',
        render: (_, row) => (
          <div className="leading-tight">
            <div className="font-medium text-gray-800 whitespace-nowrap">{row.security_name || '—'}</div>
            {row.isin ? (
              <div className="text-xs text-gray-500 font-mono mt-0.5 whitespace-nowrap">{row.isin}</div>
            ) : null}
          </div>
        ),
      },
      {
        title: 'Qty × Price',
        key: 'qty_price',
        align: 'right',
        render: (_, row) => (
          <span className="whitespace-nowrap">
            {row.quantity ?? '—'} × {formatCurrency(row.price_per_share)}
          </span>
        ),
      },
      {
        title: 'Total Payable',
        dataIndex: 'total_payable',
        key: 'total_payable',
        align: 'right',
        render: (value) => <span className="font-semibold whitespace-nowrap">{formatCurrency(value)}</span>,
      },
      {
        title: 'Paid',
        dataIndex: 'total_paid_amount',
        key: 'total_paid_amount',
        align: 'right',
        render: (value) => <span className="whitespace-nowrap">{formatCurrency(value)}</span>,
      },
      {
        title: 'Payment',
        dataIndex: 'payment_status',
        key: 'payment_status',
        width: 110,
        render: (status) => (
          <span
            className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${
              status === 'PAID'
                ? 'bg-green-50 text-green-700'
                : status === 'PARTIAL'
                  ? 'bg-amber-50 text-amber-700'
                  : 'bg-gray-100 text-gray-600'
            }`}
          >
            {PAYMENT_STATUS_LABELS[status] || status || '—'}
          </span>
        ),
      },
      {
        title: 'Type',
        dataIndex: 'transaction_type',
        key: 'transaction_type',
        width: 160,
        render: (type, row) => (
          <span className="whitespace-nowrap text-sm">
            {row.transaction_type_label || TRANSACTION_TYPE_LABELS[type] || type || '—'}
          </span>
        ),
      },
    ],
    []
  );

  return (
    <Card className="rounded-xl" title="Purchase Deal Sheets">
      {error ? (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      ) : null}

      <Table
        rowKey={(row) => row.uuid || row.id}
        columns={columns}
        dataSource={items}
        loading={loading}
        pagination={false}
        scroll={{ x: 960 }}
        locale={{ emptyText: <Empty description="No transactions found for this supplier." /> }}
        onRow={(row) => ({
          onClick: () => {
            const id = row.uuid || row.id;
            if (id) navigate(`/pre-ipo/purchase-deal-sheet/${id}`);
          },
          className: 'cursor-pointer hover:bg-gray-50',
        })}
      />

      <div className="flex justify-end mt-4">
        <Pagination
          current={page}
          pageSize={pageSize}
          total={pagination?.total ?? 0}
          showSizeChanger
          pageSizeOptions={['10', '20', '50']}
          onChange={(nextPage, nextSize) => {
            setPage(nextPage);
            if (nextSize !== pageSize) {
              setPageSize(nextSize);
              setPage(1);
            }
          }}
          showTotal={(total, range) => (
            <span className="text-xs text-gray-500">
              {range[0]}–{range[1]} of {total}
            </span>
          )}
        />
      </div>
    </Card>
  );
}
